/**
 *  Auth class
 *
 *  Cette classe permet la gestion de l'authentification de l'utilisateur
 *
 *  ------------------------------------------------------------
 *
 *  # Dépendances
 *
 *  Cette class requiert :
 *      - Axios (pour la méthode fetchUser)
 *      - vue-cookie (pour écrire/supprimer le cookie d'authentification )
 *
 *  ------------------------------------------------------------
 *
 *  # Comment un user est considéré comme authentifié ?
 *
 *  L'user est considéré comme authentifié si le cookie (cookieName) existe.
 *  Lorsque l'user s'authentifie sur la page /login le cookie est créé avec :
 *      - le token d'accès à l'api
 *      - le slug de l'organisation
 *      - les roles de l'utilisateur
 *      - le slug utilisateur
 *
 *  ## Sérialisation du cookie
 *
 *  Les informations son enregistré dans le cookie sous la forme d'un objet sérialisé.
 *  IL FAUT DONC UTILISER LES GETTERS POUR ACCÉDER AUX DONNÉES CONTENU DANS LE COOKIE.
 *
 *   ---
 *
 *  ## Récupération de l'utilisateur
 *
 *  Cette classe propose une méthode fetchUser et fetchOrganizationUser pour récupérer l'user et son organization.
 *
 *  ## Dés-authentification
 *  Il suffit d'appeler la méthode removeCookie()
 *
 *  ---
 *
 */
export default class Auth {

    constructor(vueCookie, http, sentry, store) {

        /**
         * Axios ($http)
         */
        this.http = http;

        /**
         * vue-cookie ($cookie)
         */

        this.vueCookie = vueCookie;

        /**
         * sentry ($sentry)
         */
        this.sentry = sentry;

        /**
         * store ($store)
         */
        this.store = store;

        /**
         * Le token d'accès à l'api
         * @type {string}
         */
        this.token = '';

        /**
         * Le refresh token pour rafraîchir le token de l'api
         * @type {string}
         */
        this.refreshToken = '';

        /**
         * le slug de l'organization (requis pour la récupération de l'utilisateur)
         * @type {string}
         */
        this.organization = '';

        /**
         * Les rôles de l'utilisateur
         * @type {Array}
         */
        this.roles = [];

        /**
         * Le nom du cookie d'authentification
         * @type {string}
         */
        this.cookieName = 'auth';

        /**
         * La durée de validité du cookie d'authentification (en jours)
         * @type {string}
         */
        this.cookieExpire = 7;


        /**
         * Le domain du cookie d'authentification
         * @type {string}
         * @deprecated Je laisse commenté au cas ou besoin de le ré-implémenter plus tard mais j'ai eu trop d'ereur en localhost pour le moment donc.
         */
        // this.cookieDomain = process.env.VUE_APP_DOMAIN;

        /**
         * Le slug de l'utilisateur (requis pour la récupération de l'utilisateur)
         * @type {string}
         */
        this.userSlug = '';

        /**
         * La hiérarchie des rôles
         * (permet de retourner true via la méthode hasRole() lorsque l'utilisateur a un rôle supérieur à celui exigé)
         *
         * @type {{ROLE_USER: number, ROLE_ADMIN: number, ROLE_OWNER: number}}
         */
        this.roleHierarchy = {
            ROLE_USER: 1,
            ROLE_ADMIN: 2,
            ROLE_OWNER: 3,
        }

        /*
            Indique la connexion en cours est une impersonation
            @type {Bool}
         */
        this.impInProgress = false;

        /*
            Un array de slug de workspaces
            @type {Array}
         */
        this.workspaces = [];

        /*
            Le slug du workspace actuellement actif pour l'utilisateur
            @type {String} @todo fusionner avec activeWorkspaceCurrency dans le ticket #290
         */
        this.activeWorkspace = '';


        /*
            La devise du workspace (au format iso 4217 ex: 'USD, EUR')
            @type {String} @todo fusionner avec activeWorkspace dans le ticket #290
         */
        this.activeWorkspaceCurrency = '';
    }

    /*
        Indique si l'user est authentifié
     */
    isAuthenticated() {
        // si le cookie existe alors le user est authentifié
        if (this.vueCookie.get(this.cookieName) != null) {
            return true
        }
        else {
            return false
        }
    }

    /////////////////////////////////////////////////////////////////
    ////////// METHODS
    /////////////////////////////////////////////////////////////////

    /**
     * Retourne true si l'user a le role 'roleRequis' ou supérieur
     *
     * @param roleRequis String Le role requis ('ROLE_USER/ROLE_ADMIN/ROLE_OWNER') @see Auth.roleHierarchy
     */
    hasRole(roleRequis) {

        let self = this;
        let accessGranted = false;
        // on itère sur les roles
        this.getRoles().forEach(function (element) {
            if (element == roleRequis) {
                accessGranted = true;
            }
            else if (self.roleHierarchy[element] > self.roleHierarchy[roleRequis])
            // si le role est supérieur dans la hiérarchie on autorise
            {
                accessGranted = true;
            }

        });

        if (accessGranted){
            return true
        }
    }


    /**
     *  Enregistre le cookie pour marquer l'utilisateur comme authentifié
     */
    saveCookie() {

        let cookieContent = {
            'token': this.token,
            'refreshToken': this.refreshToken,
            'organization': this.organization,
            'roles': this.roles,
            'userSlug': this.userSlug,
            'impInProgress': this.impInProgress,
            'workspaces': this.workspaces,
            'activeWorkspace' :this.activeWorkspace,
            'activeWorkspaceCurrency' :this.activeWorkspaceCurrency,
        };

        //création du cookie
        this.vueCookie.set(
            this.cookieName,
            JSON.stringify(cookieContent),
            {expires: this.cookieExpire,
                // domain: this.cookieDomain
            }
        );

    }

    /**
     * Pour dés-authentifier l'utilisateur
     *
     * Supprime le cookie d'authentification
     */
    removeCookie() {
        this.vueCookie.delete(this.cookieName)

    }


    /**
     * Retourne le contenu du header Authorization pour l'utilisateur.
     * Pour les requêtes à l'api.
     *
     * @returns {string}
     */
    header() {
        return `Bearer ${this.getToken()}`
    }

    /////////////////////////////////////////////////////////////////
    ////////// SETTERS
    /////////////////////////////////////////////////////////////////

    /**
     *
     * @param token
     * @returns {Auth}
     */
    setToken(token) {
        this.token = token;
        return this
    }

    /**
     *
     * @param refreshToken
     * @returns {Auth}
     */
    setRefreshToken(refreshToken) {
        this.refreshToken = refreshToken;
        return this
    }

    /**
     *
     * @param organization
     * @returns {Auth}
     */
    setOrganization(organization) {
        this.organization = organization;
        return this
    }

    /**
     *
     * @param roles
     * @returns {Auth}
     */
    setRoles(roles) {
        this.roles = roles;
        return this
    }

    /**
     *
     * @param slug
     * @returns {Auth}
     */
    setUserSlug(slug) {
        this.userSlug = slug;
        return this
    }

    /**
     *
     * @param value
     * @returns {Auth}
     */
    setImpInProgress(value) {
        this.impInProgress = value;
        return this
    }

    /**
     *
     * @param workspaces
     * @returns {Auth}
     */
    setWorkspaces(workspaces) {
        this.workspaces = workspaces;
        return this
    }

    /**
     *
     * @param slug
     * @returns {Auth}
     */
    setActiveWorkspace(value) {
        this.activeWorkspace = value;
        return this
    }

    /**
     *
     * @param slug
     * @returns {Auth}
     */
    setActiveWorkspaceCurrency(value) {
        this.activeWorkspaceCurrency = value;
        return this
    }
/////////////////////////////////////////////////////////////////
    ////////// GETTERS
    /////////////////////////////////////////////////////////////////

    /**
     *
     * @returns {*}
     */
    getToken() {
        return JSON.parse(this.vueCookie.get(this.cookieName)).token;
    }
    /**
     *
     * @returns {*}
     */
    getRefreshToken() {
        return JSON.parse(this.vueCookie.get(this.cookieName)).refreshToken;
    }

    /**
     *
     * @returns {string|organization|{name}|string|*}
     */
    getOrganization() {
        return JSON.parse(this.vueCookie.get(this.cookieName)).organization;
    }


    /**
     *
     * @returns {string|*}
     */
    getUserSlug() {
        return JSON.parse(this.vueCookie.get(this.cookieName)).userSlug;
    }

    /**
     *
     * @returns {array|*}
     */
    getRoles() {
        return JSON.parse(this.vueCookie.get(this.cookieName)).roles;
    }


    /**
     *
     * @returns {boolean}
     */
    getImpInProgress() {
        return JSON.parse(this.vueCookie.get(this.cookieName)).impInProgress;
    }

    /**
     *
     * @returns {array|*}
     */
    getWorkspaces() {
        return JSON.parse(this.vueCookie.get(this.cookieName)).workspaces;
    }

    /**
     *
     * @returns {array|*}
     */
    getActiveWorkspace() {
        return JSON.parse(this.vueCookie.get(this.cookieName)).activeWorkspace;
    }
    /**
     *
     * @returns {array|*}
     */
    getActiveWorkspaceCurrency() {
        return JSON.parse(this.vueCookie.get(this.cookieName)).activeWorkspaceCurrency;
    }

}
